import {
  ArrowDownIcon,
  BirthdayCakeIcon,
  CitizenCardIssueDateIcon,
  CitizenIdCardIcon,
  MapMarkerIcon,
  NeutralIcon,
  PhoneIcon
} from 'assets';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { ContactDto } from 'types/dto/contacts';
import CustomerLookupContactHistoryItemHistories from './CustomerLookupContactHistoryItemHistories';
import { AIContactEvaluationOption } from 'utils';
import dayjs from 'dayjs';
import { Spin } from 'antd';

type InfoItem = {
  icon: React.ReactNode;
  value?: string;
};

type ProfileInfoProps = {
  items: InfoItem[];
  className?: string;
};

export const ProfileInfo: React.FC<ProfileInfoProps> = ({ items, className }) => {
  return (
    <div className={twMerge(className, 'flex flex-wrap items-start gap-x-2')}>
      <span>
        {items
          .filter((o) => !!o.value)
          .map(({ icon, value }, index) => {
            return (
              <React.Fragment key={index}>
                {index > 0 && <span className='mx-2 mb-1 inline-block h-1 w-1 rounded-full bg-subTextColor' />}
                {icon && <span className='mr-2 inline-block translate-y-0.5'>{icon}</span>}
                <span className='whitespace-normal break-words text-sm'>{value}</span>
              </React.Fragment>
            );
          })}
      </span>
    </div>
  );
};

export const CustomerCodes = ({
  customerCodes,
  onCustomerClick
}: {
  customerCodes?: string[];
  onCustomerClick?: (customerCode: string) => void;
}) => {
  return (
    <div className={twMerge('flex flex-wrap')}>
      {customerCodes?.map((code, index) => (
        <span
          key={index}
          className={twMerge(
            'mr-2 rounded-md bg-colorPrimary px-1 text-sm font-semibold leading-5 text-white',
            onCustomerClick && 'cursor-pointer hover:bg-[#00248C] hover:underline'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onCustomerClick?.(code);
          }}
        >
          {code}
        </span>
      ))}
    </div>
  );
};

type CustomerLookupContactHistoryItemProps = {
  item?: ContactDto;
  className?: string;
  open?: boolean;
  onExpandChange?: (contactId: number | null) => void;
  onCustomerClick?: (customerCode: string) => void;
  onContactClick?: (contactId: number) => void;
  isLoadingContactSentiment?: boolean;
};

const CustomerLookupContactHistoryItem: React.FC<CustomerLookupContactHistoryItemProps> = ({
  item,
  open,
  onExpandChange,
  onCustomerClick,
  onContactClick,
  isLoadingContactSentiment
}) => {
  const infoList = [
    { icon: <PhoneIcon className='h-4 w-4 text-[#b2b9c4]' />, value: item?.phoneNumber },
    { icon: <MapMarkerIcon className='h-4 w-4 text-[#b2b9c4]' />, value: item?.address }
  ];
  const moreInfoList = [
    { icon: <CitizenIdCardIcon className='h-4 w-4 text-[#b2b9c4]' />, value: item?.idNumber },
    {
      icon: <CitizenCardIssueDateIcon className='h-4 w-4 text-[#b2b9c4]' />,
      value: item?.idIssuedDate ? dayjs(item.idIssuedDate).format('DD/MM/YYYY') : undefined
    },
    {
      icon: <BirthdayCakeIcon className='h-4 w-4 text-[#b2b9c4]' />,
      value: item?.birthday ? dayjs(item.birthday).format('DD/MM/YYYY') : undefined
    },
    {
      icon: null,
      value: item?.idIssuedPlace
    }
  ];

  const optionEvaluation = item?.contactSentiment?.sentiment
    ? AIContactEvaluationOption[item?.contactSentiment?.sentiment]
    : undefined;

  return (
    <div
      onClick={() => {
        if (item?.contactId) onContactClick?.(item?.contactId);
      }}
      className={twMerge(
        'w-full cursor-pointer overflow-hidden rounded-md border bg-white px-4 hover:bg-colorBgItemHover',
        open && 'bg-[#f5f5f5]'
      )}
    >
      <div className={twMerge('flex w-full items-start gap-4 py-4')}>
        <div className='flex h-12 w-12 items-center justify-center'>
          <Spin spinning={isLoadingContactSentiment}>
            {optionEvaluation ? optionEvaluation?.icon(48) : <NeutralIcon height={48} width={48} />}
          </Spin>
        </div>

        <div className='flex flex-1 flex-col overflow-hidden'>
          <span className='mb-1 font-semibold text-colorPrimary'>{item?.name}</span>

          <ProfileInfo items={infoList} />

          <div className={twMerge('grid transition-all', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
            <div className='overflow-hidden'>{<ProfileInfo items={moreInfoList} />}</div>
          </div>
          <div className={twMerge('grid transition-all', !open ? 'mt-3 grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
            <div className='overflow-hidden'>
              {<CustomerCodes onCustomerClick={onCustomerClick} customerCodes={item?.customerCodes} />}
            </div>
          </div>
        </div>

        <div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onExpandChange?.(open ? null : item?.contactId || null);
            }}
            className={twMerge(
              'mt-1 flex h-8 w-8 cursor-pointer select-none items-center justify-center rounded-full border transition-all',
              open && 'rotate-180'
            )}
          >
            <ArrowDownIcon />
          </div>
        </div>
      </div>

      <div className={`grid transition-all ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className='overflow-hidden border-t'>
          <div>
            {open && (
              <CustomerLookupContactHistoryItemHistories
                onCustomerClick={onCustomerClick}
                contactId={item?.contactId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLookupContactHistoryItem;
